import React, { useMemo, useState } from 'react'
import { makeStyles } from '@mui/styles'
import { useCallback } from 'react'
import { LocalizationProvider, PickersShortcutsItem } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DateRange, DateRangePicker, SingleInputDateRangeField } from '@mui/x-date-pickers-pro'
import dayjs, { Dayjs } from 'dayjs'
import { DEFAULT_DATE_RANGE_LAST_YEAR, DEFAULT_DATE_RANGE_TODAY, DEFAULT_DATE_RANGE_YTD, VIEWS } from '@/global'
import InputAdornment from '@mui/material/InputAdornment'
import ArrowDropUp from '@mui/icons-material/ArrowDropUp'
import ArrowDropDown from '@mui/icons-material/ArrowDropDown'

const useStyles = makeStyles(() => ({
  root: {
    width: 223,
  },
  input: {
    color: '#211F1F',
    borderRadius: 4,
    fontSize: 20,
    height: 56,
    paddingRight: '9px !important',
  },
}))

interface Props {
  value?: DateRange<Dayjs>
  options?: any[]
  onChange?: (value: DateRange<Dayjs>) => void
  disabled?: boolean
  defaultValue?: any
  label?: string
  className?: string
  style?: React.CSSProperties
  variant?: 'standard' | 'outlined' | 'filled'
  placeholder?: string
  allowFuture?: boolean
  /**
   * Earliest selectable date — the account's go-live date on analytics screens,
   * so the picker cannot offer a window the backend would clamp away.
   */
  minDate?: Dayjs
  /**
   * Pull a shortcut preset that starts before minDate up to it, instead of the picker
   * greying the preset out: on an account that started in August, YTD reads August 1 →
   * today rather than being unavailable.
   */
  clampShortcutsToMin?: boolean
  // Override the built-in shortcut presets (e.g. to match the app-wide VIEWS list). Falls back to the defaults when omitted.
  shortcuts?: PickersShortcutsItem<DateRange<Dayjs>>[]
  /**
   * An empty range means every date on this screen (DESIGN_GUIDE.md 7.5): the picker
   * leads with an "All time" shortcut that clears the range, and clearing the field
   * reports `[null, null]` instead of being ignored. Pair with placeholder="All time".
   */
  allTime?: boolean
}

const allTimeShortcut: PickersShortcutsItem<DateRange<Dayjs>> = {
  label: 'All time',
  getValue: () => [null, null],
}

const shortcutsItems: PickersShortcutsItem<DateRange<Dayjs>>[] = [
  {
    label: 'Today',
    getValue: () => {
      return DEFAULT_DATE_RANGE_TODAY
    },
  },
  {
    label: 'WTD',
    getValue: () => {
      const today = dayjs()
      return [today.startOf('week'), today]
    },
  },
  {
    label: 'MTD',
    getValue: () => {
      const today = dayjs()
      return [today.startOf('month'), today]
    },
  },
  {
    label: 'Last 7 Days',
    getValue: () => {
      const today = dayjs()
      return [today.subtract(7, 'day'), today]
    },
  },
  {
    label: 'Last 30 Days',
    getValue: () => {
      const today = dayjs()
      return [today.subtract(30, 'day'), today]
    },
  },
  {
    label: 'Last 60 Days',
    getValue: () => {
      const today = dayjs()
      return [today.subtract(60, 'day'), today]
    },
  },
  {
    label: 'Last 90 Days',
    getValue: () => {
      const today = dayjs()
      return [today.subtract(90, 'day'), today]
    },
  },
  {
    label: 'Last 12 Months',
    getValue: () => {
      return DEFAULT_DATE_RANGE_LAST_YEAR
    },
  },
  {
    label: 'YTD',
    getValue: () => {
      return DEFAULT_DATE_RANGE_YTD
    },
  },
  {
    label: 'Last 18 Months',
    getValue: () => {
      return VIEWS.find((item) => item.value === 'Last 18 Months')?.range as DateRange<Dayjs>
    },
  },
  {
    label: 'Last 24 Months',
    getValue: () => {
      return VIEWS.find((item) => item.value === 'Last 24 Months')?.range as DateRange<Dayjs>
    },
  },
]

const futureShortcutsItems: PickersShortcutsItem<DateRange<Dayjs>>[] = [
  {
    label: 'Next 30 Days',
    getValue: () => {
      const today = dayjs()
      return [today.startOf('day'), today.add(30, 'day').endOf('day')]
    },
  },
  ...shortcutsItems,
]

const CustomDateRange: React.FC<Props> = ({
  label = 'Date Range',
  value,
  disabled,
  onChange,
  defaultValue,
  className,
  style,
  variant = 'outlined',
  placeholder,
  allowFuture,
  minDate,
  clampShortcutsToMin,
  shortcuts,
  allTime,
}) => {
  const classes = useStyles()
  const [open, setOpen] = useState(false)
  const shortcutItems = useMemo(() => {
    const presets = shortcuts || (allowFuture ? futureShortcutsItems : shortcutsItems)
    const items = allTime ? [allTimeShortcut, ...presets] : presets
    if (!clampShortcutsToMin || !minDate) return items
    const clamp = (date: Dayjs | null) => (date && date.isBefore(minDate) ? minDate : date)
    return items.map((item) => ({
      ...item,
      getValue: (params: Parameters<typeof item.getValue>[0]): DateRange<Dayjs> => {
        const [start, end] = item.getValue(params)
        return [clamp(start), clamp(end)]
      },
    }))
  }, [shortcuts, allowFuture, allTime, clampShortcutsToMin, minDate])

  function getElementByText() {
    const elements1 = document.getElementsByClassName('MuiDateRangeCalendar-root')
    const elements = elements1?.[0]?.childNodes || []
    for (let i = 0; i < elements.length; i++) {
      if (elements[i].textContent === 'MUI X Missing license key') {
        return elements[i]
      }
    }
    return null
  }

  const onChangeDate = useCallback(
    (date: DateRange<Dayjs>) => {
      if (date.length === 2 && date[0] && date[1]) {
        date[0] = date[0].hour(0).minute(0).second(0)
        date[1] = date[1].hour(23).minute(59).second(59)
        onChange?.(date)
      } else if (allTime && !date[0] && !date[1]) {
        // The "All time" shortcut, or the field cleared: back to every date.
        onChange?.([null, null])
      }
    },
    [onChange, allTime]
  )
  const onOpen = () => {
    setOpen(true)
    setTimeout(() => {
      // remove Licensing
      const list = getElementByText()
      if (list) {
        // @ts-ignore
        list.style.display = 'none'
      }
    }, 0)
  }
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DateRangePicker<Dayjs>
        onChange={onChangeDate}
        label={label}
        value={value}
        slotProps={{
          shortcuts: {
            items: shortcutItems,
          },
          actionBar: { actions: [] },
          textField: {
            variant,
            sx:
              variant === 'standard'
                ? {
                    minWidth: 220,
                    ...(style || {}),
                    '& .MuiInput-underline:before': {
                      borderBottom: 'none', // 移除默认状态下划线
                    },
                    '& .MuiInput-underline:after': {
                      borderBottom: 'none', // 移除聚焦状态下划线
                    },
                    // 添加 hover 状态下的边框宽度
                    '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                      borderBottomWidth: '1px',
                    },
                  }
                : {
                    minWidth: 220,
                    ...(style || {}),
                  },
            InputProps: {
              endAdornment:
                variant === 'standard' ? (
                  <InputAdornment position="end" sx={{ cursor: 'pointer' }}>
                    {open ? <ArrowDropUp htmlColor="#666" /> : <ArrowDropDown htmlColor="#666" />}
                  </InputAdornment>
                ) : null,
              placeholder,
            },
          },
        }}
        sx={{ minWidth: 220, ...(style || {}) }}
        defaultValue={defaultValue}
        minDate={minDate}
        disableFuture={!allowFuture}
        calendars={1}
        onOpen={onOpen}
        onClose={() => setOpen(false)}
        slots={{ field: SingleInputDateRangeField }}
        name="allowedRange"
      />
    </LocalizationProvider>
  )
}

export default CustomDateRange
